import axios from "axios";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";
import {
  CreatePipelineRequest,
  CreatePipelinePermissionRequest,
  PipelinesResponse,
  PipelineResponse,
  PipelinePermissionsResponse,
  PipelinePermissionResponse,
} from "@/types/pipeline";

const API_BASE_URL = import.meta.env.VITE_PIPELINE_API_URL;
const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

export const pipelineAPI = {
  getPipelines: async (
    params: string | { slug: string; role_id?: string; is_active?: boolean },
  ): Promise<PipelinesResponse> => {
    try {
      let url = `${API_BASE_URL}/pipelines`;
      const queryParams: string[] = [];

      if (typeof params === "string") {
        queryParams.push(`slug=${params}`);
      } else {
        queryParams.push(`slug=${params.slug}`);
        if (params.role_id) {
          queryParams.push(`role_id=${params.role_id}`);
        }
        if (params.is_active !== undefined) {
          queryParams.push(`is_active=${params.is_active}`);
        }
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }

      const response = await axiosInstance.get(url, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error al obtener pipelines:", error);
      throw error;
    }
  },

  /**
   * Obtiene un pipeline específico por ID
   */
  getPipelineById: async (
    slug: string,
    pipelineId: string,
  ): Promise<PipelineResponse> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/pipelines/${pipelineId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al obtener pipeline:", error);
      throw error;
    }
  },

  /**
   * Crea un nuevo pipeline
   */
  createPipeline: async (
    slug: string,
    data: CreatePipelineRequest,
  ): Promise<PipelineResponse> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/pipelines`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al crear pipeline:", error);
      throw error;
    }
  },

  /**
   * Actualiza un pipeline existente
   */
  updatePipeline: async (
    slug: string,
    pipelineId: string,
    data: Partial<CreatePipelineRequest>,
  ): Promise<PipelineResponse> => {
    try {
      const response = await axiosInstance.put(
        `${API_BASE_URL}/pipelines/${pipelineId}`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al actualizar pipeline:", error);
      throw error;
    }
  },

  /**
   * Elimina un pipeline
   */
  deletePipeline: async (
    slug: string,
    pipelineId: string,
  ): Promise<{ success: boolean }> => {
    try {
      const response = await axiosInstance.delete(
        `${API_BASE_URL}/pipelines/${pipelineId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al eliminar pipeline:", error);
      throw error;
    }
  },

  /**
   * Obtiene los permisos de un pipeline específico
   */
  getPipelinePermissions: async (
    slug: string,
    pipelineId: string,
  ): Promise<PipelinePermissionsResponse> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/pipelines/${pipelineId}/permissions`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al obtener permisos del pipeline:", error);
      throw error;
    }
  },

  /**
   * Asigna permisos a un rol para un pipeline específico
   */
  createPipelinePermission: async (
    slug: string,
    data: CreatePipelinePermissionRequest,
  ): Promise<PipelinePermissionResponse> => {
    try {
      const response = await axiosInstance.post(
        `${API_BASE_URL}/pipelines/${data.pipeline_id}/permissions`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al crear permiso de pipeline:", error);
      throw error;
    }
  },

  /**
   * Actualiza permisos de un rol para un pipeline
   */
  updatePipelinePermission: async (
    slug: string,
    pipelineId: string,
    roleId: string,
    data: Partial<CreatePipelinePermissionRequest>,
  ): Promise<PipelinePermissionResponse> => {
    try {
      const response = await axiosInstance.put(
        `${API_BASE_URL}/pipelines/${pipelineId}/permissions/${roleId}`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al actualizar permiso de pipeline:", error);
      throw error;
    }
  },

  /**
   * Elimina un permiso de pipeline
   */
  deletePipelinePermission: async (
    slug: string,
    pipelineId: string,
    roleId: string,
  ): Promise<{ success: boolean }> => {
    try {
      const response = await axiosInstance.delete(
        `${API_BASE_URL}/pipelines/${pipelineId}/permissions/${roleId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al eliminar permiso de pipeline:", error);
      throw error;
    }
  },

  /**
   * Obtiene los pipelines asignados a un rol específico
   */
  getPipelinesByRole: async (
    slug: string,
    roleId: string,
  ): Promise<PipelinesResponse> => {
    try {
      const response = await axiosInstance.get(
        `${API_BASE_URL}/roles/${roleId}/pipelines`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error al obtener pipelines del rol:", error);
      throw error;
    }
  },
};

export default pipelineAPI;
